import clinicalSession from '../services/clinicalSession';
import { worklistFilterKeys, type Filters } from '../services/clinicalApi';

type WorklistState = { draft: Filters; filters: Filters; page: number };
const storageKey = 'ohif.clinical.worklist.v1';
const emptyState = (): WorklistState => ({ draft: {}, filters: {}, page: 1 });

function restoreState(): WorklistState {
  const session = clinicalSession.getSnapshot();
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (
      !session ||
      stored?.owner !== session.username ||
      stored?.expiresAt !== session.expiresAt ||
      !Number.isSafeInteger(stored.page) ||
      stored.page < 1
    ) {
      sessionStorage.removeItem(storageKey);
      return emptyState();
    }
    const filters = (value: unknown): Filters => {
      const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      return Object.fromEntries(
        worklistFilterKeys
          .filter(key => typeof source[key] === 'string')
          .map(key => [key, source[key] as string])
      );
    };
    return { draft: filters(stored.draft), filters: filters(stored.filters), page: stored.page };
  } catch {
    return emptyState();
  }
}

let state = restoreState();
let generation = clinicalSession.getVersion();

function persistState() {
  const session = clinicalSession.getSnapshot();
  try {
    if (session && clinicalSession.getCurrentToken()) {
      // Keep query controls for this tab/session, never records, PDFs or another token.
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ ...state, owner: session.username, expiresAt: session.expiresAt })
      );
    } else {
      sessionStorage.removeItem(storageKey);
    }
  } catch {
    // Browser storage restrictions still permit in-memory navigation state.
  }
}

clinicalSession.subscribe(() => {
  if (generation !== clinicalSession.getVersion()) {
    generation = clinicalSession.getVersion();
    state = emptyState();
  }
  persistState();
});

export function getWorklistState(): WorklistState {
  return state;
}

export function saveWorklistState(value: WorklistState) {
  if (clinicalSession.getCurrentToken()) {
    state = { draft: { ...value.draft }, filters: { ...value.filters }, page: value.page };
    persistState();
  }
}
